<template>
  <div class="email-editor-wrapper">
    <!-- Toolbar -->
    <div class="editor-toolbar">
      <!-- Text Formatting -->
      <div class="toolbar-group">
        <button
          class="toolbar-btn"
          :class="{ active: activeFormats.bold }"
          title="Bold"
          @mousedown.prevent
          @click="execCmd('bold')"
        >
          <strong>B</strong>
        </button>
        <button
          class="toolbar-btn"
          :class="{ active: activeFormats.italic }"
          title="Italic"
          @mousedown.prevent
          @click="execCmd('italic')"
        >
          <em>I</em>
        </button>
        <button
          class="toolbar-btn"
          :class="{ active: activeFormats.underline }"
          title="Underline"
          @mousedown.prevent
          @click="execCmd('underline')"
        >
          <u>U</u>
        </button>
        <button
          class="toolbar-btn"
          :class="{ active: activeFormats.strikeThrough }"
          title="Strikethrough"
          @mousedown.prevent
          @click="execCmd('strikeThrough')"
        >
          <s>S</s>
        </button>
      </div>

      <span class="toolbar-divider" />

      <!-- Font Size -->
      <div class="toolbar-group">
        <select
          v-model="selectedFontSize"
          class="toolbar-select"
          title="Font Size"
          @mousedown="saveSelection"
          @change="onFontSizeChange"
        >
          <option value="">
            Size
          </option>
          <option value="1">
            Small
          </option>
          <option value="3">
            Normal
          </option>
          <option value="4">
            Large
          </option>
          <option value="5">
            X-Large
          </option>
          <option value="7">
            Huge
          </option>
        </select>
      </div>

      <span class="toolbar-divider" />

      <!-- Colors -->
      <div class="toolbar-group">
        <label
          class="toolbar-btn color-btn"
          title="Font Color"
          @mousedown.prevent="saveSelection"
        >
          <span class="color-letter">A</span>
          <span
            class="color-bar"
            :style="{ background: fontColor }"
          />
          <input
            v-model="fontColor"
            type="color"
            class="sr-only"
            @input="applyFontColor"
          >
        </label>
        <label
          class="toolbar-btn color-btn"
          title="Highlight"
          @mousedown.prevent="saveSelection"
        >
          <feather-icon
            icon="Edit3Icon"
            size="13"
          />
          <span
            class="color-bar"
            :style="{ background: highlightColor }"
          />
          <input
            v-model="highlightColor"
            type="color"
            class="sr-only"
            @input="applyHighlightColor"
          >
        </label>
      </div>

      <span class="toolbar-divider" />

      <!-- Alignment -->
      <div class="toolbar-group">
        <button
          class="toolbar-btn"
          title="Align Left"
          @mousedown.prevent
          @click="execCmd('justifyLeft')"
        >
          <feather-icon
            icon="AlignLeftIcon"
            size="14"
          />
        </button>
        <button
          class="toolbar-btn"
          title="Align Center"
          @mousedown.prevent
          @click="execCmd('justifyCenter')"
        >
          <feather-icon
            icon="AlignCenterIcon"
            size="14"
          />
        </button>
        <button
          class="toolbar-btn"
          title="Align Right"
          @mousedown.prevent
          @click="execCmd('justifyRight')"
        >
          <feather-icon
            icon="AlignRightIcon"
            size="14"
          />
        </button>
      </div>

      <span class="toolbar-divider" />

      <!-- Lists -->
      <div class="toolbar-group">
        <button
          class="toolbar-btn"
          title="Bullet List"
          @mousedown.prevent
          @click="execCmd('insertUnorderedList')"
        >
          <feather-icon
            icon="ListIcon"
            size="14"
          />
        </button>
        <button
          class="toolbar-btn"
          title="Numbered List"
          @mousedown.prevent
          @click="execCmd('insertOrderedList')"
        >
          <feather-icon
            icon="HashIcon"
            size="14"
          />
        </button>
      </div>

      <span class="toolbar-divider" />

      <!-- Insert -->
      <div class="toolbar-group">
        <button
          class="toolbar-btn"
          title="Insert Link"
          @mousedown.prevent
          @click="showLinkModal = true"
        >
          <feather-icon
            icon="LinkIcon"
            size="14"
          />
        </button>
        <button
          class="toolbar-btn"
          title="Insert Table"
          @mousedown.prevent
          @click="showTableModal = true"
        >
          <feather-icon
            icon="GridIcon"
            size="14"
          />
        </button>
        <button
          class="toolbar-btn"
          title="Horizontal Rule"
          @mousedown.prevent
          @click="insertHorizontalRule"
        >
          <feather-icon
            icon="MinusIcon"
            size="14"
          />
        </button>
      </div>

      <span class="toolbar-divider" />

      <!-- Templates: button that morphs into a search input -->
      <div class="toolbar-group">
        <div class="dropdown-wrapper">
          <!-- Button state -->
          <button
            v-if="!showTemplates"
            class="toolbar-btn toolbar-btn-text"
            title="Templates"
            @click="openTemplatesSearch"
          >
            <feather-icon
              icon="LayoutIcon"
              size="13"
            />
            <span>Templates</span>
          </button>

          <!-- Search input state -->
          <div
            v-else
            class="template-search-wrapper"
          >
            <feather-icon
              icon="SearchIcon"
              size="13"
              class="template-search-icon"
            />
            <input
              ref="templateSearchInput"
              v-model="templateSearchQuery"
              type="text"
              class="template-search-input"
              placeholder="Search templates..."
              @input="onTemplateSearchInput"
            >
            <feather-icon
              icon="XIcon"
              size="13"
              class="template-search-close"
              @click="closeTemplatesSearch"
            />
          </div>

          <!-- Dropdown results -->
          <div
            v-if="showTemplates"
            class="dropdown-menu-custom dropdown-menu-wide"
          >
            <div
              v-if="templatesLoading"
              class="dropdown-status"
            >
              Loading...
            </div>
            <div
              v-else-if="templatesError"
              class="dropdown-status text-danger"
            >
              {{ templatesError }}
            </div>
            <div
              v-else-if="templates.length === 0"
              class="dropdown-status text-muted"
            >
              No templates found
            </div>
            <button
              v-for="tpl in templates"
              v-else
              :key="tpl.id || tpl.name"
              class="dropdown-item-custom"
              @click="applyTemplate(tpl)"
            >
              <strong>{{ tpl.name }}</strong>
              <small
                v-if="tpl.description"
                class="text-muted d-block"
              >{{ tpl.description }}</small>
            </button>
          </div>
        </div>
      </div>
      <span class="toolbar-divider" />

      <!-- name Dropdown -->
      <div class="toolbar-group">
        <button
          class="toolbar-btn toolbar-btn-text"
          title="Insert Name Placeholder"
          @click="insertNamePlaceholder"
        >
          <feather-icon
            icon="UserIcon"
            size="13"
          />
          <span>Name</span>
        </button>
      </div>

      <!-- Right-side actions -->
      <div class="toolbar-spacer" />

      <div class="toolbar-group">
        <button
          class="toolbar-btn toolbar-btn-text"
          :class="{ active: signatureInserted }"
          title="Signature"
          @click="toggleSignature"
        >
          <feather-icon
            icon="CheckSquareIcon"
            size="13"
          />
          <span>Signature</span>
        </button>
        <button
          class="toolbar-btn toolbar-btn-text"
          :class="{ active: showHtmlSource }"
          title="HTML Source"
          @click="toggleHtmlView"
        >
          <feather-icon
            icon="CodeIcon"
            size="13"
          />
          <span>HTML</span>
        </button>
      </div>
    </div>

    <!-- Editor Body -->
    <div
      ref="editorBody"
      class="editor-body"
    >
      <div
        v-show="!showHtmlSource"
        ref="editorContent"
        class="editor-content"
        contenteditable="true"
        @input="onContentInput"
        @mouseup="onEditorMouseUp"
        @keyup="onEditorKeyUp"
        @paste="onPaste"
      />
      <textarea
        v-show="showHtmlSource"
        ref="htmlSource"
        v-model="htmlContent"
        class="html-source"
        spellcheck="false"
        @input="onHtmlSourceInput"
      />

      <!-- Floating Table Toolbar -->
      <div
        v-if="tableToolbar.visible"
        class="table-toolbar"
        :style="{ top: tableToolbar.top + 'px', left: tableToolbar.left + 'px' }"
        @mousedown.prevent
      >
        <div class="table-toolbar-group">
          <button
            class="table-toolbar-btn"
            title="Insert row above"
            @click="addRowAbove"
          >
            <feather-icon
              icon="ArrowUpIcon"
              size="12"
            />
            <span>Row</span>
          </button>
          <button
            class="table-toolbar-btn"
            title="Insert row below"
            @click="addRowBelow"
          >
            <feather-icon
              icon="ArrowDownIcon"
              size="12"
            />
            <span>Row</span>
          </button>
          <button
            class="table-toolbar-btn table-toolbar-btn-danger"
            title="Delete row"
            @click="deleteRow"
          >
            <feather-icon
              icon="Trash2Icon"
              size="12"
            />
            <span>Row</span>
          </button>
        </div>

        <span class="table-toolbar-divider" />

        <div class="table-toolbar-group">
          <button
            class="table-toolbar-btn"
            title="Insert column left"
            @click="addColumnLeft"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              size="12"
            />
            <span>Col</span>
          </button>
          <button
            class="table-toolbar-btn"
            title="Insert column right"
            @click="addColumnRight"
          >
            <feather-icon
              icon="ArrowRightIcon"
              size="12"
            />
            <span>Col</span>
          </button>
          <button
            class="table-toolbar-btn table-toolbar-btn-danger"
            title="Delete column"
            @click="deleteColumn"
          >
            <feather-icon
              icon="Trash2Icon"
              size="12"
            />
            <span>Col</span>
          </button>
        </div>

        <span class="table-toolbar-divider" />

        <div class="table-toolbar-group">
          <button
            class="table-toolbar-btn table-toolbar-btn-danger"
            title="Delete table"
            @click="deleteTable"
          >
            <feather-icon
              icon="XIcon"
              size="12"
            />
            <span>Table</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Click overlay to close dropdowns -->
    <div
      v-if="showTemplates"
      class="click-overlay"
      @click="closeTemplatesSearch"
    />

    <!-- Insert Link Modal -->
    <b-modal
      v-model="showLinkModal"
      title="Insert Link"
      size="sm"
      ok-title="Insert"
      @ok="confirmInsertLink"
      @hidden="linkUrl = 'https://'"
    >
      <b-form-group
        label="URL"
        label-size="sm"
      >
        <b-form-input
          v-model="linkUrl"
          size="sm"
          placeholder="https://example.com"
        />
      </b-form-group>
    </b-modal>

    <!-- Insert Table Modal -->
    <b-modal
      v-model="showTableModal"
      title="Insert Table"
      size="sm"
      ok-title="Insert"
      @ok="confirmInsertTable"
      @hidden="tableRows = 3; tableCols = 3"
    >
      <b-row>
        <b-col>
          <b-form-group
            label="Rows"
            label-size="sm"
          >
            <b-form-input
              v-model.number="tableRows"
              type="number"
              size="sm"
              min="1"
              max="20"
            />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            label="Columns"
            label-size="sm"
          >
            <b-form-input
              v-model.number="tableCols"
              type="number"
              size="sm"
              min="1"
              max="10"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Confirm Template Replace Modal -->
    <b-modal
      v-model="showConfirmModal"
      title="Replace Content"
      size="sm"
      ok-title="Yes, Replace"
      ok-variant="danger"
      cancel-title="Cancel"
      @ok="confirmApplyTemplate"
      @hidden="pendingTemplate = null"
    >
      <p>This will replace your current content. Are you sure?</p>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal, BFormGroup, BFormInput, BRow, BCol,
} from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'EmailEditor',

  components: {
    BModal,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
  },

  props: {
    value: {
      type: String,
      default: '',
    },
    customPlaceholders: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      htmlContent: '',
      showHtmlSource: false,
      showTemplates: false,
      signatureInserted: false,
      selectedFontSize: '',
      fontColor: '#000000',
      highlightColor: '#ffff00',
      activeFormats: {
        bold: false,
        italic: false,
        underline: false,
        strikeThrough: false,
      },

      // Templates from backend
      templates: [],
      templatesLoading: false,
      templatesError: null,
      templateSearchQuery: '',
      templateSearchDebounceTimer: null,

      // Modal data
      showLinkModal: false,
      linkUrl: 'https://',
      showTableModal: false,
      tableRows: 3,
      tableCols: 3,
      // Confirm template replace modal
      showConfirmModal: false,
      pendingTemplate: null,
      // Saved selection range for color pickers
      savedRange: null,

      // Floating table toolbar
      tableToolbar: {
        visible: false,
        top: 0,
        left: 0,
      },
      activeCell: null,
    }
  },

  computed: {
    signatureHtml() {
      /* eslint-disable max-len */
      return `<div style="margin-top:24px; padding-top:16px; border-top:2px solid #0178BA;">
  <table cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif; font-size:13px; color:#333;">
    <tr>
      <td style="padding-right:14px; vertical-align:middle;">
        <img src="https://erp.vatikadistributors.com/static/email_signature/logo.png" width="150" height="52" alt="Vatika Distributors" style="display:block; border:0;" />
      </td>
      <td style="vertical-align:top; padding:0 14px; border-left:2px solid #0178BA; border-right:2px solid #0178BA;">
        <div style="font-size:16px; font-weight:900; color:#E5172C; margin-bottom:0; letter-spacing:0.5px; text-transform:uppercase;">Vatika Distributors Pvt. Ltd.</div>
        <div style="font-size:9px; font-weight:700; color:#0178BA; letter-spacing:2px; text-transform:uppercase; margin-bottom:8px;">Pharmaceutical Wholesalers</div>
        <table cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif; font-size:11.5px; color:#333; border-collapse:collapse; width:100%;">
          <tr>
            <td style="padding:2px 0; vertical-align:middle; width:16px;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/phone.png" width="12" height="12" alt="" style="display:block; border:0;" />
            </td>
            <td style="padding:2px 0; font-weight:700; color:#333; width:65px;">Sales</td>
            <td style="padding:2px 0; white-space:nowrap;"><a href="tel:06573500333" style="color:#0178BA; text-decoration:none; font-weight:500;">0657-3500333</a></td>
            <td style="padding:2px 0 2px 10px; white-space:nowrap;">
              <a href="mailto:sales@vatikadistributors.com" style="color:#0178BA; text-decoration:none;">
                <img src="https://erp.vatikadistributors.com/static/email_signature/email_icon.png" width="11" height="11" alt="" style="display:inline; vertical-align:middle; margin-right:3px; border:0;" /><span style="font-size:10.5px;">sales@vatikadistributors.com</span>
              </a>
            </td>
          </tr>
          <tr>
            <td style="padding:2px 0; vertical-align:middle;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/phone.png" width="12" height="12" alt="" style="display:block; border:0;" />
            </td>
            <td style="padding:2px 0; font-weight:700; color:#333;">Accounts</td>
            <td style="padding:2px 0;"><a href="tel:06573500332" style="color:#0178BA; text-decoration:none; font-weight:500;">0657-3500332</a></td>
            <td style="padding:2px 0 2px 10px;">
              <a href="mailto:accounts@vatikadistributors.com" style="color:#0178BA; text-decoration:none;">
                <img src="https://erp.vatikadistributors.com/static/email_signature/email_icon.png" width="11" height="11" alt="" style="display:inline; vertical-align:middle; margin-right:3px; border:0;" /><span style="font-size:10.5px;">accounts@vatikadistributors.com</span>
              </a>
            </td>
          </tr>
          <tr>
            <td style="padding:2px 0; vertical-align:middle;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/phone.png" width="12" height="12" alt="" style="display:block; border:0;" />
            </td>
            <td style="padding:2px 0; font-weight:700; color:#333;">Expiry</td>
            <td style="padding:2px 0;"><a href="tel:06573500331" style="color:#0178BA; text-decoration:none; font-weight:500;">0657-3500331</a></td>
            <td style="padding:2px 0 2px 10px;">
              <a href="mailto:expiry@vatikadistributors.com" style="color:#0178BA; text-decoration:none;">
                <img src="https://erp.vatikadistributors.com/static/email_signature/email_icon.png" width="11" height="11" alt="" style="display:inline; vertical-align:middle; margin-right:3px; border:0;" /><span style="font-size:10.5px;">expiry@vatikadistributors.com</span>
              </a>
            </td>
          </tr>
          <tr>
            <td style="padding:2px 0; vertical-align:middle;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/phone.png" width="12" height="12" alt="" style="display:block; border:0;" />
            </td>
            <td style="padding:2px 0; font-weight:700; color:#333;">Purchase</td>
            <td style="padding:2px 0;"><a href="tel:06573500330" style="color:#0178BA; text-decoration:none; font-weight:500;">0657-3500330</a></td>
            <td style="padding:2px 0 2px 10px;">
              <a href="mailto:purchase@vatikadistributors.com" style="color:#0178BA; text-decoration:none;">
                <img src="https://erp.vatikadistributors.com/static/email_signature/email_icon.png" width="11" height="11" alt="" style="display:inline; vertical-align:middle; margin-right:3px; border:0;" /><span style="font-size:10.5px;">purchase@vatikadistributors.com</span>
              </a>
            </td>
          </tr>
        </table>
      </td>
      <td style="vertical-align:top; padding-left:14px;">
        <table cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif; font-size:11.5px; color:#333; border-collapse:collapse;">
          <tr>
            <td style="padding:0 0 12px 0; vertical-align:middle; width:20px;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/whatsapp.png" width="16" height="16" alt="WhatsApp" style="display:block; border:0;" />
            </td>
            <td style="padding:0 0 12px 4px;">
              <a href="https://wa.me/919204251917" target="_blank" style="color:#25D366; text-decoration:none; font-weight:700; font-size:13px;">9204251917</a>
            </td>
          </tr>
          <tr>
            <td style="padding:0; vertical-align:top;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/pin.png" width="14" height="14" alt="" style="display:block; border:0;" />
            </td>
            <td style="padding:0 0 0 4px; color:#555; line-height:1.5; font-size:11px;">
              76 A, Pennar Road,<br>SNP Area, Sakchi,<br>Jamshedpur-831001
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</div>`
      /* eslint-enable max-len */
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val !== this.htmlContent) {
          this.htmlContent = val
          this.$nextTick(() => {
            if (this.$refs.editorContent && this.$refs.editorContent.innerHTML !== val) {
              this.$refs.editorContent.innerHTML = val
            }
          })
        }
      },
    },
  },

  mounted() {
    if (this.value) {
      this.$refs.editorContent.innerHTML = this.value
      this.htmlContent = this.value
    }
    document.addEventListener('mousedown', this.onDocumentClick)
    if (this.$refs.editorBody) {
      this.$refs.editorBody.addEventListener('scroll', this.hideTableToolbar)
    }
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentClick)
    if (this.$refs.editorBody) {
      this.$refs.editorBody.removeEventListener('scroll', this.hideTableToolbar)
    }
  },

  methods: {
    // --- Core ---
    execCmd(command, val = null) {
      this.$refs.editorContent.focus()
      document.execCommand(command, false, val) // eslint-disable-line no-restricted-properties
      this.syncContent()
      this.updateActiveFormats()
    },

    syncContent() {
      this.htmlContent = this.$refs.editorContent.innerHTML
      this.$emit('input', this.htmlContent)
    },

    onContentInput() {
      this.syncContent()
    },

    onHtmlSourceInput() {
      this.$refs.editorContent.innerHTML = this.htmlContent
      this.$emit('input', this.htmlContent)
    },

    onPaste(e) {
      e.preventDefault()
      const html = e.clipboardData.getData('text/html')
      const text = e.clipboardData.getData('text/plain')
      this.execCmd('insertHTML', html || text)
    },

    onEditorMouseUp(e) {
      this.updateActiveFormats()
      this.detectTableCell(e.target)
    },

    onEditorKeyUp(e) {
      this.updateActiveFormats()
      // Re-detect cell when navigating with arrow keys inside a table
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0) {
        const node = sel.anchorNode
        const cell = this.findParentCell(node)
        if (cell) {
          this.activeCell = cell
          this.positionTableToolbar(cell)
        } else if (this.tableToolbar.visible) {
          this.hideTableToolbar()
        }
      }
      // eslint-disable-next-line no-unused-expressions
      e
    },

    updateActiveFormats() {
      this.activeFormats.bold = document.queryCommandState('bold')
      this.activeFormats.italic = document.queryCommandState('italic')
      this.activeFormats.underline = document.queryCommandState('underline')
      this.activeFormats.strikeThrough = document.queryCommandState('strikeThrough')
    },

    // --- Font Size ---
    onFontSizeChange() {
      if (this.selectedFontSize) {
        this.restoreSelection()
        this.execCmd('fontSize', this.selectedFontSize)
        this.selectedFontSize = ''
      }
    },

    // --- Selection Save/Restore ---
    saveSelection() {
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0) {
        this.savedRange = sel.getRangeAt(0).cloneRange()
      }
    },

    restoreSelection() {
      if (this.savedRange) {
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(this.savedRange)
      }
    },

    // --- Colors ---
    applyFontColor() {
      this.restoreSelection()
      this.$refs.editorContent.focus()
      document.execCommand('foreColor', false, this.fontColor) // eslint-disable-line no-restricted-properties
      this.syncContent()
    },

    applyHighlightColor() {
      this.restoreSelection()
      this.$refs.editorContent.focus()
      document.execCommand('hiliteColor', false, this.highlightColor) // eslint-disable-line no-restricted-properties
      this.syncContent()
    },

    // --- Insert Link (via modal) ---
    confirmInsertLink() {
      if (this.linkUrl && this.linkUrl !== 'https://') {
        this.$refs.editorContent.focus()
        this.execCmd('createLink', this.linkUrl)
      }
    },

    // --- Insert Table (via modal) ---
    confirmInsertTable() {
      const rows = this.tableRows || 3
      const cols = this.tableCols || 3
      let html = '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse; border-color:#ddd; width:100%; margin:8px 0;">'
      for (let r = 0; r < rows; r += 1) {
        html += '<tr>'
        for (let c = 0; c < cols; c += 1) {
          html += r === 0
            ? '<th style="background:#f0f0f0; text-align:left; padding:8px;">&nbsp;</th>'
            : '<td style="padding:8px;">&nbsp;</td>'
        }
        html += '</tr>'
      }
      html += '</table><p><br></p>'
      this.$refs.editorContent.focus()
      this.execCmd('insertHTML', html)
    },

    insertHorizontalRule() {
      this.execCmd('insertHTML', '<hr style="border:none; border-top:1px solid #ccc; margin:12px 0;">')
    },

    // --- Name Placeholder ---
    insertNamePlaceholder() {
      this.$refs.editorContent.focus()
      const span = '<span class="name-placeholder" style="background:#eeedfd; color:#7367f0; padding:1px 6px; border-radius:3px; font-family:monospace; font-size:12px;" contenteditable="false">name-placeholder</span>&nbsp;'
      this.execCmd('insertHTML', span)
    },

    // --- Table Editing ---
    findParentCell(node) {
      let current = node
      while (current && current !== this.$refs.editorContent) {
        if (current.nodeType === 1
          && (current.tagName === 'TD' || current.tagName === 'TH')) {
          return current
        }
        current = current.parentNode
      }
      return null
    },

    detectTableCell(target) {
      const cell = this.findParentCell(target)
      if (cell) {
        this.activeCell = cell
        this.positionTableToolbar(cell)
      } else {
        this.hideTableToolbar()
      }
    },

    positionTableToolbar(cell) {
      const table = cell.closest('table')
      if (!table || !this.$refs.editorBody) return

      const bodyRect = this.$refs.editorBody.getBoundingClientRect()
      const tableRect = table.getBoundingClientRect()
      const { scrollTop } = this.$refs.editorBody

      // Position toolbar just above the table, relative to editor body
      let top = (tableRect.top - bodyRect.top) + scrollTop - 36
      const left = Math.max(8, (tableRect.left - bodyRect.left))

      // If table is at the very top, place toolbar below the table instead
      if (top < scrollTop + 4) {
        top = (tableRect.bottom - bodyRect.top) + scrollTop + 6
      }

      this.tableToolbar.top = top
      this.tableToolbar.left = left
      this.tableToolbar.visible = true
    },

    hideTableToolbar() {
      this.tableToolbar.visible = false
      this.activeCell = null
    },

    onDocumentClick(e) {
      // Hide toolbar if click is outside editor body and outside the toolbar itself
      if (!this.$refs.editorBody) return
      const insideEditor = this.$refs.editorBody.contains(e.target)
      const insideToolbar = e.target.closest && e.target.closest('.table-toolbar')
      if (!insideEditor && !insideToolbar) {
        this.hideTableToolbar()
      }
    },

    getCellIndex(cell) {
      const row = cell.parentNode
      return Array.prototype.indexOf.call(row.children, cell)
    },

    buildEmptyCell(isHeader) {
      const cell = document.createElement(isHeader ? 'th' : 'td')
      if (isHeader) {
        cell.setAttribute('style', 'background:#f0f0f0; text-align:left; padding:8px;')
      } else {
        cell.setAttribute('style', 'padding:8px;')
      }
      cell.innerHTML = '&nbsp;'
      return cell
    },

    addRowAbove() {
      if (!this.activeCell) return
      const row = this.activeCell.parentNode
      const newRow = document.createElement('tr')
      const cellCount = row.children.length
      const isHeaderRow = row.children[0] && row.children[0].tagName === 'TH'
      for (let i = 0; i < cellCount; i += 1) {
        newRow.appendChild(this.buildEmptyCell(isHeaderRow))
      }
      row.parentNode.insertBefore(newRow, row)
      this.syncContent()
      this.$nextTick(() => this.positionTableToolbar(this.activeCell))
    },

    addRowBelow() {
      if (!this.activeCell) return
      const row = this.activeCell.parentNode
      const newRow = document.createElement('tr')
      const cellCount = row.children.length
      // New row is always a body row (td), even if current row is header
      for (let i = 0; i < cellCount; i += 1) {
        newRow.appendChild(this.buildEmptyCell(false))
      }
      if (row.nextSibling) {
        row.parentNode.insertBefore(newRow, row.nextSibling)
      } else {
        row.parentNode.appendChild(newRow)
      }
      this.syncContent()
      this.$nextTick(() => this.positionTableToolbar(this.activeCell))
    },

    deleteRow() {
      if (!this.activeCell) return
      const row = this.activeCell.parentNode
      const table = row.closest('table')
      if (!table) return
      // Don't delete the last remaining row; remove the entire table instead
      const totalRows = table.querySelectorAll('tr').length
      if (totalRows <= 1) {
        this.deleteTable()
        return
      }
      row.parentNode.removeChild(row)
      this.activeCell = null
      this.hideTableToolbar()
      this.syncContent()
    },

    addColumnLeft() {
      if (!this.activeCell) return
      const colIndex = this.getCellIndex(this.activeCell)
      const table = this.activeCell.closest('table')
      if (!table) return
      const rows = table.querySelectorAll('tr')
      rows.forEach(row => {
        const isHeaderRow = row.children[0] && row.children[0].tagName === 'TH'
        const newCell = this.buildEmptyCell(isHeaderRow)
        const ref = row.children[colIndex]
        if (ref) {
          row.insertBefore(newCell, ref)
        } else {
          row.appendChild(newCell)
        }
      })
      this.syncContent()
      this.$nextTick(() => this.positionTableToolbar(this.activeCell))
    },

    addColumnRight() {
      if (!this.activeCell) return
      const colIndex = this.getCellIndex(this.activeCell)
      const table = this.activeCell.closest('table')
      if (!table) return
      const rows = table.querySelectorAll('tr')
      rows.forEach(row => {
        const isHeaderRow = row.children[0] && row.children[0].tagName === 'TH'
        const newCell = this.buildEmptyCell(isHeaderRow)
        const ref = row.children[colIndex + 1]
        if (ref) {
          row.insertBefore(newCell, ref)
        } else {
          row.appendChild(newCell)
        }
      })
      this.syncContent()
      this.$nextTick(() => this.positionTableToolbar(this.activeCell))
    },

    deleteColumn() {
      if (!this.activeCell) return
      const colIndex = this.getCellIndex(this.activeCell)
      const table = this.activeCell.closest('table')
      if (!table) return
      // If this is the only column, remove the entire table
      const firstRow = table.querySelector('tr')
      if (firstRow && firstRow.children.length <= 1) {
        this.deleteTable()
        return
      }
      const rows = table.querySelectorAll('tr')
      rows.forEach(row => {
        const target = row.children[colIndex]
        if (target) row.removeChild(target)
      })
      this.activeCell = null
      this.hideTableToolbar()
      this.syncContent()
    },

    deleteTable() {
      if (!this.activeCell) return
      const table = this.activeCell.closest('table')
      if (table && table.parentNode) {
        table.parentNode.removeChild(table)
      }
      this.hideTableToolbar()
      this.syncContent()
    },

    // --- Templates (backend-loaded with search) ---
    openTemplatesSearch() {
      this.showTemplates = true
      this.templateSearchQuery = ''
      // Focus the input once it renders
      this.$nextTick(() => {
        if (this.$refs.templateSearchInput) {
          this.$refs.templateSearchInput.focus()
        }
      })
      // Load all templates initially
      this.fetchTemplates('')
    },

    closeTemplatesSearch() {
      this.showTemplates = false
      this.templateSearchQuery = ''
      if (this.templateSearchDebounceTimer) {
        clearTimeout(this.templateSearchDebounceTimer)
        this.templateSearchDebounceTimer = null
      }
    },

    onTemplateSearchInput() {
      if (this.templateSearchDebounceTimer) {
        clearTimeout(this.templateSearchDebounceTimer)
      }
      this.templateSearchDebounceTimer = setTimeout(() => {
        this.fetchTemplates(this.templateSearchQuery.trim())
      }, 400)
    },

    fetchTemplates(search) {
      this.templatesLoading = true
      this.templatesError = null

      const params = {}
      if (search) params.search = search

      axios.get('/emails/email_templates/', { params })
        .then(res => {
          // Handle both paginated and non-paginated responses
          if (Array.isArray(res.data)) {
            this.templates = res.data
          } else {
            this.templates = res.data.results || []
          }
          this.templatesLoading = false
        })
        .catch(error => {
          this.templatesError = error?.response?.data?.error || 'Failed to load templates'
          this.templatesLoading = false
        })
    },

    applyTemplate(tpl) {
      const text = this.$refs.editorContent.innerHTML.replace(/<[^>]*>/g, '').trim()
      if (text.length > 0) {
        this.pendingTemplate = tpl
        this.showConfirmModal = true
        this.closeTemplatesSearch()
        return
      }
      this.setTemplateContent(tpl)
    },

    confirmApplyTemplate() {
      if (this.pendingTemplate) {
        this.setTemplateContent(this.pendingTemplate)
        this.pendingTemplate = null
      }
    },

    setTemplateContent(tpl) {
      // Backend sends HTML in `content` field; keep fallback for legacy `html`
      const htmlContent = tpl.content || tpl.html || ''
      this.$refs.editorContent.innerHTML = htmlContent
      this.syncContent()
      this.closeTemplatesSearch()
    },

    // --- Signature ---
    toggleSignature() {
      const editor = this.$refs.editorContent
      if (this.signatureInserted) {
        const sig = editor.querySelector('[data-signature="true"]')
        if (sig) sig.remove()
        this.signatureInserted = false
      } else {
        editor.innerHTML += `<div data-signature="true">${this.signatureHtml}</div>`
        this.signatureInserted = true
      }
      this.syncContent()
    },

    // --- HTML Toggle ---
    toggleHtmlView() {
      if (this.showHtmlSource) {
        this.$refs.editorContent.innerHTML = this.htmlContent
      } else {
        this.htmlContent = this.$refs.editorContent.innerHTML
      }
      this.showHtmlSource = !this.showHtmlSource
      this.hideTableToolbar()
    },

    // --- Public API ---
    getHtml() { return this.htmlContent },
    setHtml(html) {
      this.htmlContent = html
      this.$refs.editorContent.innerHTML = html
      this.$emit('input', html)
    },
    getPlainText() { return this.$refs.editorContent.innerText },
  },
}
</script>

<style scoped>
.email-editor-wrapper {
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  overflow: hidden;
  background: #fff;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: #f8f8f8;
  border-bottom: 1px solid #ebe9f1;
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 1px;
}

.toolbar-divider {
  display: inline-block;
  width: 1px;
  height: 22px;
  background: #ebe9f1;
  margin: 0 4px;
}

.toolbar-spacer {
  flex: 1;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 7px;
  border: none;
  background: transparent;
  border-radius: 0.25rem;
  cursor: pointer;
  color: #6e6b7b;
  transition: all 0.15s;
  font-size: 13px;
  font-weight: 600;
  font-family: Georgia, serif;
  line-height: 1;
}
.toolbar-btn:hover {
  background: #ebe9f1;
  color: #5e5873;
}
.toolbar-btn.active {
  background: #7367f0;
  color: #fff;
}

.toolbar-btn-text {
  font-family: inherit;
  font-weight: 500;
}
.toolbar-btn-text span {
  font-size: 11px;
  white-space: nowrap;
}

.toolbar-select {
  padding: 3px 6px;
  border: 1px solid #ebe9f1;
  border-radius: 0.25rem;
  font-size: 11px;
  color: #6e6b7b;
  background: #fff;
  cursor: pointer;
  outline: none;
  font-family: inherit;
}

.color-btn {
  position: relative;
  cursor: pointer;
  padding: 3px 6px;
  flex-direction: column;
  gap: 1px;
}
.color-letter {
  font-size: 14px;
  font-weight: 800;
  font-family: Georgia, serif;
  line-height: 1;
}
.color-bar {
  width: 16px;
  height: 3px;
  border-radius: 1px;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.dropdown-wrapper {
  position: relative;
}

/* Template search input (replaces the Templates button) */
.template-search-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid #7367f0;
  border-radius: 0.25rem;
  background: #fff;
  height: 26px;
  min-width: 200px;
}
.template-search-icon {
  color: #7367f0;
  flex-shrink: 0;
}
.template-search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  color: #5e5873;
  flex: 1;
  padding: 0;
  font-family: inherit;
}
.template-search-input::placeholder {
  color: #b9b9c3;
}
.template-search-close {
  color: #b9b9c3;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s;
}
.template-search-close:hover {
  color: #ea5455;
}

.dropdown-menu-custom {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 100;
  min-width: 200px;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  box-shadow: 0 5px 25px rgba(34, 41, 47, 0.1);
  padding: 4px;
  margin-top: 4px;
  max-height: 320px;
  overflow-y: auto;
}
.dropdown-menu-wide {
  min-width: 250px;
}
.dropdown-item-custom {
  display: block;
  width: 100%;
  padding: 6px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 0.25rem;
  text-align: left;
  font-size: 13px;
  color: #6e6b7b;
  transition: background 0.1s;
}
.dropdown-item-custom:hover {
  background: #f8f8f8;
}
.dropdown-item-custom code {
  font-size: 12px;
  color: #7367f0;
  background: #eeedfd;
  padding: 1px 5px;
  border-radius: 3px;
}
.dropdown-status {
  padding: 10px;
  font-size: 12px;
  text-align: center;
  color: #6e6b7b;
}

.click-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
}

.editor-body {
  position: relative;
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
}

.editor-content {
  min-height: 300px;
  padding: 14px 16px;
  outline: none;
  font-size: 14px;
  line-height: 1.7;
  color: #6e6b7b;
}
.editor-content:empty::before {
  content: 'Start typing your email...';
  color: #b9b9c3;
  pointer-events: none;
}

.editor-content:deep(table) {
  border-collapse: collapse;
  width: 100%;
}
.editor-content:deep(td),
.editor-content:deep(th) {
  border: 1px solid #ddd;
  padding: 8px;
}

.html-source {
  width: 100%;
  min-height: 300px;
  padding: 14px 16px;
  border: none;
  outline: none;
  resize: vertical;
  font-family: 'Fira Code', 'Cascadia Code', monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #6e6b7b;
  background: #f8f8f8;
  tab-size: 2;
  box-sizing: border-box;
}

/* Floating Table Toolbar */
.table-toolbar {
  position: absolute;
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  box-shadow: 0 4px 16px rgba(34, 41, 47, 0.12);
  white-space: nowrap;
}
.table-toolbar-group {
  display: flex;
  align-items: center;
  gap: 1px;
}
.table-toolbar-divider {
  display: inline-block;
  width: 1px;
  height: 18px;
  background: #ebe9f1;
  margin: 0 3px;
}
.table-toolbar-btn {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px 6px;
  border: none;
  background: transparent;
  border-radius: 0.25rem;
  cursor: pointer;
  color: #6e6b7b;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  transition: all 0.15s;
}
.table-toolbar-btn:hover {
  background: #eeedfd;
  color: #7367f0;
}
.table-toolbar-btn-danger:hover {
  background: #fde9e9;
  color: #ea5455;
}
.table-toolbar-btn span {
  font-size: 11px;
}
</style>
